(function () {

    'use strict';

    if (typeof window === 'undefined') {

        module.exports = exports(
            require('./metric.js'),
            require('../../metrics/defaults.js'),
            require('../../metrics/crossChannel.js'),
            require('lodash')
        );

    } else {
        RivalIQ.metric.search = exports(
            RivalIQ.metric,
            RivalIQ.metrics.defaults,
            RivalIQ.metrics.definitions.crossChannel,
            window._
        );
    }

    /*
     * This module provides functions for retrieving metric metadata
     * information including name, description, format, type and more.
     *
     * Definitions are defined in public/metrics in a tree form where branches
     * are dimensions and leaves are properties.
     *
     * The functions here explore that tree and return the most specific values
     * defined there. See the tests for this file as well as the other contents
     * of public/modules/metrics for more examples
     */
    function exports(metricFn, defaults, definitions, _) {

        // Returns true if this metric definition is explicitly defined
        function exists(dimension) {
            return function (tree, unexploredDimensions, fixedValue) {
                if (_.includes(unexploredDimensions, dimension)) {
                    return;
                }

                return fixedValue;
            };
        }

        // Pulls a dimension value out of the definition tree
        function extractDimension(dimension, key) {
            const keys = _.isArray(key) ? key : [key];
            let value;

            return function (tree, unexploredDimensions, fixedValue, parentDimension) {
                // Only return if the root of this tree is the dimension we are
                // searching for.
                if (parentDimension !== dimension) {
                    return;
                }

                for (let i = 0; i < keys.length; i++) {
                    value = _.get(tree, [keys[i]]);

                    if (!_.isUndefined(value)) {
                        return value;
                    }
                }
            };
        }

        // Returns a key value for a given property. If the tree has a
        // value -> {val} -> {key} entry that is preferred over {key}
        function extractKey(property, key, data) {
            const keys = _.isArray(key) ? key : [key];
            const val = _.isArray(data) ? _.last(data) : data;
            let value;

            return function (tree) {
                for (let i = 0; i < keys.length; i++) {
                    if (!_.isUndefined(val)) {
                        value = _.get(tree, [property, 'value', val, keys[i]]);
                    }

                    if (_.isUndefined(value)) {
                        value = _.get(tree, [property, keys[i]]);
                    }

                    if (!_.isUndefined(value)) {
                        return value;
                    }
                }
            };
        }

        /*
         *  Traverse the metric definition tree depth first by dimension,
         *  calling the evaluator fn at each node. The returned result is
         *  the result of the first evaluator fn that returns a value other
         *  than undefined
         *
         *  Static dimension values of the form {dimension} -> value ->
         *  {dimensionValue} are preferred over {dimension}
         */
        function findMetricValue(dimensions, metric, evaluator) {
            dimensions = dimensions || [];

            function exploreNode(tree, dimensions, fixedValue, parentDimension) {
                for (let i = 0; i < dimensions.length; i++) {
                    const dimension = dimensions[i];

                    if (!tree[dimension]) {
                        continue;
                    }

                    let value;
                    const treeLocation = _.get(tree, [dimension, 'value', metric[dimension]]);
                    if (treeLocation) {
                        value = exploreNode(treeLocation, _.without(dimensions, dimension), true, dimension);
                    }

                    if (_.isUndefined(value)) {
                        value = exploreNode(tree[dimension], _.without(dimensions, dimension), false, dimension);
                    }

                    if (!_.isUndefined(value)) {
                        return value;
                    }
                }

                return evaluator(tree, dimensions, fixedValue, parentDimension);
            }

            return exploreNode(definitions, dimensions);
        }

        function applyDimension(attrs, dimension, value) {
            attrs[dimension] = value;
            return attrs;
        }

        /*
         * Get a value for a metric key given a metric definition
         * Keys include things like name, description, format, valueType, etc
         *
         * TODO/NOTE this method does not behave correctly for certain
         * combinations of "query," notably where `key` is `type` and the
         * `property` or `returns` is `name`. It's unclear how to correct it.
         * For more information see #14845
         */
        function getPropertyValue(metric, key, data, options) {
            const dimensions = metric.dimensions || [];
            metric = metricFn.assign(metric, data);

            const evaluator = extractKey(metric.returns || metric.property, key, data);
            let value = findMetricValue(dimensions, metric, evaluator);

            if (_.isUndefined(value)) {
                value = getDefault(key);
            }

            if (_.isFunction(value)) {
                return value(data, metricFn(metric), options);
            }

            return value;
        }

        /*
         * Get a value for a dimension key given a metric definition
         * Keys include things like name, format, etc
         */
        function getDimensionValue(metric, key, dimension, value) {
            if (!_.isUndefined(value)) {
                metric = metricFn.pin(metricFn.build(metric, applyDimension({}, dimension, value)));
            }
            const evaluator = extractDimension(dimension, key);
            return findMetricValue(metric.dimensions, metric, evaluator);
        }

        /*
         * Returns true if  a full tree path with explicit values is defined
         */
        function isSpecified(metric, dimension, value) {
            metric = metricFn.pin(metricFn.build(metric, applyDimension({}, dimension, value)));

            const evaluator = exists(dimension);
            return findMetricValue(metric.dimensions, metric, evaluator);
        }

        function getDefault(key) {
            return defaults.metric[key];
        }

        function getDimensionDefault(key) {
            return defaults.dimension[key];
        }

        return {
            getDefault: getDefault,
            getDimensionDefault: getDimensionDefault,
            findMetricValue: findMetricValue,
            getPropertyValue: getPropertyValue,
            getDimensionValue: getDimensionValue,
            isSpecified: isSpecified,
            extractionFn: {
                extractKey: extractKey,
                extractDimension: extractDimension,
                exists: exists
            }
        };
    }
})();
